import { queryOptions, useQuery } from '@tanstack/react-query'
import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
} from 'src/enums'
import {
  OrganizationIntegrationListModel,
  OrganizationIntegrationListResponse,
} from 'src/models/organizationIntegration.models'
import { MutationMethod, useExtendMutation } from 'src/queries/queries.utils'

import { orgIntegrationsQueryKeys } from '../queries.constants'
import {
  getOrgIntegrationDetail,
  getOrgIntegrationsList,
} from './integrations/integrations.network'

interface OrgIntegrationApiResponse {
  organizationConnection: OrganizationIntegrationListResponse
}

interface OrgIntegrationsApiResponse {
  organizationConnections: OrganizationIntegrationListResponse[]
}

type UseQueryOrgIntegrationsResult = Partial<
  Record<OrganizationConnectedPlatform, OrganizationIntegrationListModel>
>

const selectOrgIntegrationModel = (
  data: OrgIntegrationApiResponse,
): OrganizationIntegrationListModel =>
  new OrganizationIntegrationListModel(data.organizationConnection)

const selectOrgIntegrationModels = (
  data: OrgIntegrationsApiResponse,
): UseQueryOrgIntegrationsResult =>
  data.organizationConnections.reduce<UseQueryOrgIntegrationsResult>(
    (acc, conn) => {
      const platform = conn.connectedPlatform as OrganizationConnectedPlatform
      acc[platform] = new OrganizationIntegrationListModel(conn)
      return acc
    },
    {},
  )

export const orgIntegrationQueries = {
  detail: (orgId: string | undefined, orgIntegrationId: string | undefined) =>
    queryOptions({
      queryKey: orgIntegrationsQueryKeys.detail(orgIntegrationId),
      queryFn: (): Promise<OrgIntegrationApiResponse> => {
        if (!orgId || !orgIntegrationId) {
          throw new Error(
            'orgIntegrationQueries.detail: orgId and orgIntegrationId are required',
          )
        }
        return getOrgIntegrationDetail(orgId, orgIntegrationId)
      },
      select: selectOrgIntegrationModel,
    }),
  list: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgIntegrationsQueryKeys.all,
      queryFn: (): Promise<OrgIntegrationsApiResponse> => {
        if (!orgId) {
          throw new Error('orgIntegrationQueries.list: orgId is required')
        }
        return getOrgIntegrationsList(orgId)
      },
      select: selectOrgIntegrationModels,
    }),
}

interface QueryOrgIntegrationsOptions {
  orgId: string | undefined
  enabled?: boolean
}

export const useActiveOrgIntegration = (
  options: QueryOrgIntegrationsOptions,
) => {
  const { orgId, enabled = true } = options

  const result = useQuery({ ...orgIntegrationQueries.list(orgId), enabled })
  const activeIntegration = result.data
    ? Object.values(result.data).find(
        (integration) =>
          integration.connectionStatus === OrganizationIntegrationStatus.ACTIVE,
      )
    : undefined
  return { ...result, data: activeIntegration }
}

interface ReauthConnectionResponse {
  companyToken: string
  linkUrl: string
  accessToken: string
}

interface UseReauthOrgIntegrationRequest {
  body: Record<string, never>
  orgId: string
  connectionId: string
}

export const useReauthOrgIntegration = () => {
  return useExtendMutation<
    ReauthConnectionResponse,
    UseReauthOrgIntegrationRequest
  >(MutationMethod.POST, {
    mutationUrl: ({ orgId, connectionId }) =>
      `/organizations/${orgId}/connections/${connectionId}/reauth`,
  })
}
